"use client";

import { Plus, RefreshCw, Trash2 } from "lucide-react";

import LomdaUploadFile from "../shared/lomdaUploadFile";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/localization";
import { HowToBlock as HowToBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import { createHowToStep } from "../../../lib/blockRegistry";
import RichText from "../text/richText";
import RichHtml from "./richHtml";
import { IMAGE_UPLOAD } from "../../../lib/uploadLimits";

export default function HowToBlock({ block }: { block: HowToBlockType }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);

    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const { steps } = block.data;

    const setSteps = (next: typeof steps) =>
        replaceBlockData(block.id, { steps: next });
    const patchStep = (id: string, patch: Partial<(typeof steps)[number]>) =>
        patchBlockData(block.id, "how-to", (d) => ({
            steps: d.steps.map((s) => (s.id === id ? { ...s, ...patch } : s)),
        }));

    if (!editable) {
        const filled = steps.filter((s) => s.title || s.body || s.image);
        if (filled.length === 0) return null;
        return (
            <ol className="flex flex-col" style={{ gap: gridGap }}>
                {filled.map((s, i) => (
                    <li key={s.id} className="flex gap-4">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary lomda-v3-text-small text-primary-foreground">
                            {i + 1}
                        </span>
                        <div className="flex flex-1 flex-col gap-2 pt-1">
                            {s.title && (
                                <h4 className="lomda-v3-text-h5">{s.title}</h4>
                            )}
                            <RichHtml html={s.body} />
                            {s.image && (
                                /* eslint-disable-next-line @next/next/no-img-element */
                                <img
                                    src={s.image}
                                    alt={s.title}
                                    className="mt-1 max-h-72 w-full rounded-xl border object-cover"
                                />
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {steps.map((s, i) => (
                <div
                    key={s.id}
                    className="lomda-v3-card-surface flex gap-3 rounded-2xl border p-4"
                >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary lomda-v3-text-small text-primary-foreground">
                        {i + 1}
                    </span>
                    <div className="flex flex-1 flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Input
                                value={s.title}
                                onChange={(e) =>
                                    patchStep(s.id, { title: e.target.value })
                                }
                                placeholder={t("lomda-v3-editor.how-to.title-placeholder")}
                                className="lomda-v3-page-text h-8 flex-1 font-semibold"
                            />
                            <button
                                type="button"
                                aria-label={t("lomda-v3-editor.block.delete")}
                                onClick={() =>
                                    setSteps(steps.filter((x) => x.id !== s.id))
                                }
                                className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                            </button>
                        </div>
                        <RichText
                            initialHtml={s.body}
                            editable
                            useGlobalToolbar
                            placeholder={t("lomda-v3-editor.how-to.body-placeholder")}
                            onCommit={(body) => patchStep(s.id, { body })}
                        />
                        {s.image ? (
                            <div className="group/img relative w-fit">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={s.image}
                                    alt={s.title}
                                    className="max-h-48 rounded-xl border object-cover"
                                />
                                <button
                                    type="button"
                                    title={t("lomda-v3-editor.how-to.replace-image")}
                                    onClick={() => patchStep(s.id, { image: "" })}
                                    className="absolute inset-0 flex items-center justify-center rounded-xl bg-foreground/50 text-background opacity-0 transition group-hover/img:opacity-100"
                                >
                                    <RefreshCw className="size-5" />
                                </button>
                            </div>
                        ) : (
                            <div className="w-44">
                                <LomdaUploadFile
                                    reuse="image"
                                    reuseLayout="compact"
                                    options={{
                                        ...IMAGE_UPLOAD,
                                        showSelectedFileName: false,
                                        title: {
                                            text: "",
                                            keyName: "lomda-v3-editor.how-to.add-image",
                                        },
                                        subtitle: {
                                            text: "",
                                            keyName: "lomda-v3-editor.empty",
                                        },
                                    }}
                                    onUploadFinished={(file) =>
                                        patchStep(s.id, { image: file.fileUrl })
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setSteps([...steps, createHowToStep()])}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed py-2.5 lomda-v3-text-small text-muted-foreground transition hover:border-primary hover:text-primary"
            >
                <Plus className="size-4" />
                {t("lomda-v3-editor.how-to.add")}
            </button>
        </div>
    );
}
